import { consume } from '@lit/context';
import { LitElement, html } from 'lit';
import { customElement, property, query, state } from 'lit/decorators.js';
import { inputResponseContext } from '../contexts/inputResponse';
import { sourceContext } from '../contexts/source';
import { formDataContext } from '../contexts/formData';
import { BackendFormResponse, Producer } from '../util/api';
import { RateQuoteFormData, ENROLLMENT_TO_SELF_QUESTION } from '../util/form';
import { EVENT } from '../util/events';
import { planActionApi, planProducerActionApi, applyNowApi, submitSaveQuote } from '../util/api';
import { CURRENT_YEAR, SWITCH_ON_AEP } from '../util/config';
import { getIshopForm, ISHOP_FORM_ID } from '../util/form';
import { SOURCE } from '../util/source';
import { getPlanName } from '../util/plans';

const sleep = (ms: number): Promise<void> => new Promise((resolve) => setTimeout(resolve, ms));

// Pull a hidden input's value out of the applyNow.do auto-submit HTML (e.g. issApp.quoteId).
const extractHiddenInputValue = (html: string, inputName: string): string => {
    const pattern = new RegExp(`name="${inputName}"\\s*value="([^"]*)"`, 'i');
    return html.match(pattern)?.[1]?.trim() || '';
};

// Legacy session (set by the prior quote call) can lag behind on the server; retry briefly if the form isn't ready yet.
const submitApplyNowSelection = async (planId: string): Promise<string> => {
    const attemptDelaysMs = [0, 400, 900];
    for (const delayMs of attemptDelaysMs) {
        if (delayMs) await sleep(delayMs);
        try {
            const body = new URLSearchParams({ selection: planId });
            const applyNowResponse = await fetch(applyNowApi, {
                method: 'POST',
                headers: { 'Content-Type': 'application/x-www-form-urlencoded; charset=UTF-8' },
                body,
                credentials: 'include',
            });
            const applyNowResponseText = await applyNowResponse.text();
            if (applyNowResponse.ok && applyNowResponseText.includes('AgentAuthControl.action')) {
                return extractHiddenInputValue(applyNowResponseText, 'issApp.quoteId');
            }
        } catch {
            // try again on the next iteration; fall through to final empty return
        }
    }
    return '';
};

@customElement('rq-hidden-form')
export class HiddenForm extends LitElement {
    @consume({ context: inputResponseContext, subscribe: true })
    data!: BackendFormResponse;

    @consume({ context: sourceContext, subscribe: true })
    @state()
    source!: SOURCE;

    @consume({ context: formDataContext, subscribe: true })
    formData!: RateQuoteFormData;

    @query('#rqApplyForm')
    form!: HTMLFormElement;

    @state()
    planId!: string;

    @property()
    quoteId?: string;

    @property()
    userId?: string;

    @property({ attribute: false })
    agentInfo?: Producer;

    connectedCallback(): void {
        super.connectedCallback();
        window.addEventListener(EVENT.SELECT_PLAN, this.submitSelectPlan as unknown as EventListener);
    }

    disconnectedCallback(): void {
        super.disconnectedCallback();
        window.removeEventListener(EVENT.SELECT_PLAN, this.submitSelectPlan as unknown as EventListener);
    }

    get plan() {
        return this.data?.plans.find((p) => p.planId === this.planId);
    }

    private submitSelectPlan = async (e: CustomEvent) => {
        if (this.source === SOURCE.EMBEDDED_AGENT) {
            return;
        }
        if (this.source === SOURCE.PABOL) {
            const saveQuoteIshopForm = getIshopForm(ISHOP_FORM_ID.SAVE_QUOTE);
            if (saveQuoteIshopForm) {
                saveQuoteIshopForm.submit();
            } else {
                // Standalone SPA route (e.g. /get-a-quote/... via psale=y) has no legacy form - fall back to the API.
                submitSaveQuote().catch((error) => {
                    console.error('Failed to save quote: ', error);
                });
            }
        }
        const planId = e.detail.planId;
        this.planId = planId;
        if (!this.form || !this.plan) return;
        const inputs: HTMLInputElement[] = [];
        this.form['issApp.planInformation.selectedPlanName'].value = getPlanName(this.plan);
        this.form['issApp.planInformation.selectedProductFamily'].value = this.plan?.productLineCode;
        this.form['issApp.planInformation.monthlyPremium'].value = this.plan?.totalRate;
        this.form['issApp.planInformation.selectedPlanId'].value = this.plan?.bolCode;

        const eligibilityCode = (() => {
            if (SWITCH_ON_AEP && Number(this.data?.effectiveStart?.split('/').pop()) != Number(CURRENT_YEAR)) {
                return 'aep';
            }
            if (this.data?.enrollPeriod === undefined) {
                return 'sep';
            }
            return this.data.enrollPeriod;
        })();

        this.form['issApp.eligibilityCodes[0].eligibilityCodeKey'].value = eligibilityCode;

        // Set selfQuestion value with multiple fallbacks
        const selfQuestionValue =
            this.formData?.selfQuestion ||
            sessionStorage.getItem('rq_selfQuestion') ||
            (this.formData?.enrollmentFor ? ENROLLMENT_TO_SELF_QUESTION[this.formData.enrollmentFor] : null) ||
            (this.source !== SOURCE.DIRECT_SHOPPER ? 'SELF_ONLY' : '');

        // Update existing input or create new one if value exists
        const selfQuestionInput = this.form?.['issApp.selfQuestion'];
        if (selfQuestionInput && selfQuestionValue) {
            selfQuestionInput.value = selfQuestionValue;
        } else if (!selfQuestionInput && selfQuestionValue) {
            inputs.push(
                Object.assign(document.createElement('input'), {
                    type: 'hidden',
                    name: 'issApp.selfQuestion',
                    value: selfQuestionValue,
                })
            );
        }

        this.data?.applicants.forEach((a) => {
            const applicantInputs: { name: string; value?: string | boolean }[] = [
                { name: `issApp.applicants[${a.index}].dateOfBirth`, value: a.dob ?? '' },
                { name: `issApp.applicants[${a.index}].firstName`, value: a.firstName ?? '' },
                { name: `issApp.applicants[${a.index}].lastName`, value: a.lastName ?? '' },
                {
                    name: `issApp.applicants[${a.index}].relationshipToPrimary`,
                    value: a.type === 'Primary' ? 'SELF' : a.type === 'Spouse' ? 'SPOUSE' : 'CHILD',
                },
                {
                    name: `issApp.applicants[${a.index}].applicantId`,
                    value: a.index,
                },
                { name: `issApp.applicants[${a.index}].isPrimary`, value: a.type === 'Primary' },
            ];
            applicantInputs.forEach(({ name, value }) => {
                if (this.form?.[name]?.value) {
                    this.form[name].value = value;
                } else {
                    inputs.push(
                        Object.assign(document.createElement('input'), {
                            type: 'hidden',
                            name,
                            value,
                        })
                    );
                }
            });
        });

        if (
            this.data?.referralSource === 'QUOTED_SALE' ||
            this.data?.referralSource === 'WEB_QUOTE' ||
            this.data?.referralSource === 'PRODUCER_ENTERED'
        ) {
            if (sessionStorage.getItem('embeddedQuoteId')) {
                this.quoteId = sessionStorage.getItem('embeddedQuoteId') || '';
            }
            if (this.form?.['issApp.quoteId']?.value) {
                this.form['issApp.quoteId'].value = this.quoteId;
            } else {
                const quoteIdEl = Array.from(this.form.children).find(
                    (el) => (el as HTMLInputElement).name === 'issApp.quoteId'
                );
                if (!quoteIdEl && this.quoteId) {
                    inputs.push(
                        Object.assign(document.createElement('input'), {
                            type: 'hidden',
                            name: 'issApp.quoteId',
                            value: this.quoteId,
                        })
                    );
                }
            }
        }
        if (this.data?.producer) {
            const producerInputs: { name: string; value?: string }[] = [
                {
                    name: 'issApp.agentInformation.agentFullName',
                    value: `${this.data?.producer?.firstName}${this.data?.producer?.middleInitial ? ` ${this.data?.producer.middleInitial}` : ''} ${this.data?.producer?.lastName}`,
                },
                {
                    name: 'issApp.agentInformation.agentPhone',
                    value: this.data?.producer?.phone || this.data?.producer?.workPhoneNumber,
                },
                { name: 'issApp.agentInformation.agentInternalNumber', value: this.data?.producer?.producerNumber },
            ];

            if (this.data?.producer?.npn) {
                producerInputs.push({
                    name: 'issApp.agentInformation.agentNPN',
                    value: this.data?.producer?.npn,
                });
            }

            // Get userID from URL params if available
            const urlParams = new URLSearchParams(window.location.search);
            const urlUserId = urlParams.get('userID');

            producerInputs.push({
                name: 'verifyAgent.loggedInAgent',
                value:
                    urlUserId ||
                    this.userId ||
                    this.agentInfo?.agentUserId ||
                    this.data?.producer?.userID ||
                    this.agentInfo?.producerNumber ||
                    this.data?.producer?.producerNumber ||
                    '',
            });

            producerInputs.push({
                name: 'verifyAgent.agentRedirectType',
                value: 'startNew',
            });

            if (this.data?.producer?.agencyNpn) {
                producerInputs.push({
                    name: 'issApp.agentInformation.agencyInternalNumber',
                    value: this.data?.producer?.agencyNpn,
                });
                producerInputs.push({
                    name: 'issApp.agentInformation.agencyNPN',
                    value: this.data?.producer?.agencyNpn,
                });
            }
            producerInputs.forEach(({ name, value }) => {
                if (this.form?.[name]?.value) {
                    this.form[name].value = value;
                } else {
                    inputs.push(
                        Object.assign(document.createElement('input'), {
                            type: 'hidden',
                            name,
                            value,
                        })
                    );
                }
            });
        }
        if (this.data?.referralSource === 'PRODUCER_ENTERED') {
            const applyNowQuoteId = await submitApplyNowSelection(planId);
            if (applyNowQuoteId) {
                this.quoteId = applyNowQuoteId;
                if (this.form?.['issApp.quoteId']?.value !== undefined) {
                    this.form['issApp.quoteId'].value = this.quoteId;
                } else {
                    inputs.push(
                        Object.assign(document.createElement('input'), {
                            type: 'hidden',
                            name: 'issApp.quoteId',
                            value: this.quoteId,
                        })
                    );
                }
            }
        }

        inputs.forEach((input) => this.form.appendChild(input));
        this.form.submit();
    };

    render() {
        return html`<slot></slot>
            <form
                name="rqApplyForm"
                id="rqApplyForm"
                .action=${this.data?.referralSource === 'PRODUCER_ENTERED' ? planProducerActionApi : planActionApi}
                method="post"
            >
                <input type="hidden" name="newApplication" value="Y" />
                <input type="hidden" name="issApp.enrollmentType" value="NEW" />
                <input type="hidden" name="issApp.referralSource" value=${this.data?.referralSource} />
                <input type="hidden" name="issApp.eligibilityCodes[0].eligibilityCodeKey" />
                <input type="hidden" name="issApp.selfQuestion" value=${this.formData?.selfQuestion || ''} />
                <input
                    type="hidden"
                    name="issApp.formVersion"
                    value=${'MEDICAL_OFX'.concat(
                        `${Number(this.data?.effectiveStart?.split('/').pop() || CURRENT_YEAR) - 1}`
                    )}
                />
                <input type="hidden" name="issApp.planInformation.selectedPlanName" />
                <input type="hidden" name="issApp.planInformation.selectedProductFamily" />
                <input type="hidden" name="issApp.planInformation.monthlyPremium" />
                <input type="hidden" name="issApp.planInformation.selectedPlanId" />
                <input type="hidden" name="issApp.physicalAddress.county" value=${this.data?.county} />
                <input type="hidden" name="issApp.physicalAddress.zipcode" value=${this.data?.zipCode} />
                <input type="hidden" name="issApp.physicalAddress.state" value="NC" />
                <input
                    type="hidden"
                    name="issApp.planInformation.requestedEffectiveDate"
                    value=${this.data?.effectiveStart}
                />
            </form>`;
    }
}

declare global {
    interface HTMLElementTagNameMap {
        'rq-hidden-form': HiddenForm;
    }
}
